import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

interface PurchaseRow {
  id: string;
  product_type: "video" | "kit";
  product_id: string;
  product_title: string;
  price: number;
  purchased_at: string;
  progress: number | null;
  total_minutes: number | null;
  watched_minutes: number | null;
  thumbnail_url: string | null;
  category: string | null;
  provider_id: string;
  status: string;
  provider_name?: string;
}

export default function LearningProgress() {
  const { currentUser } = useApp();
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.id) { setLoading(false); return; }
    const load = async () => {
      const { data } = await supabase
        .from("purchases")
        .select("*")
        .eq("buyer_id", currentUser.id)
        .eq("status", "completed")
        .order("purchased_at", { ascending: false });
      if (!data) { setLoading(false); return; }

      const providerIds = [...new Set(data.map((p) => p.provider_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, name, service_name")
        .in("id", providerIds);
      const profileMap = Object.fromEntries((profiles || []).map((p) => [p.id, p.service_name || p.name]));

      setPurchases(data.map((p) => ({ ...p, provider_name: profileMap[p.provider_id] ?? "事業者" })));
      setLoading(false);
    };
    load();
  }, [currentUser?.id]);

  const videoItems = purchases.filter((p) => p.product_type === "video");
  const totalWatched = videoItems.reduce((a, p) => a + (p.watched_minutes ?? 0), 0);
  const inProgress = videoItems.filter((p) => (p.progress ?? 0) < 100);
  const completed = purchases.filter((p) => (p.progress ?? 0) >= 100);

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl h-24 animate-pulse border border-stone-100" />
        ))}
      </div>
    );
  }

  const getThumbnail = (p: PurchaseRow) =>
    p.thumbnail_url ||
    `https://public.readdy.ai/ai/img_res/0a60661e65d52cfeeedeafe06c6d6b3a.jpg${p.id.slice(0,8)}&orientation=landscape`;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "受講中", value: inProgress.length, icon: "ri-play-circle-line", color: "text-teal" },
          { label: "完了", value: completed.length, icon: "ri-checkbox-circle-line", color: "text-deep-green" },
          { label: "合計学習時間", value: `${totalWatched}分`, icon: "ri-time-line", color: "text-gold" },
          { label: "購入総数", value: purchases.length, icon: "ri-shopping-bag-line", color: "text-charcoal" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-stone-100">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className={`${s.icon} ${s.color} text-2xl`} />
            </div>
            <p className="font-serif text-2xl text-charcoal mt-2">{s.value}</p>
            <p className="text-xs text-charcoal/50 font-sans mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {purchases.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-stone-100">
          <i className="ri-play-circle-line text-5xl text-charcoal/20 block mb-4" />
          <p className="text-charcoal/40 font-sans text-sm mb-4">まだ購入した講座がありません</p>
          <Link to="/providers" className="bg-deep-green text-white px-6 py-2.5 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
            事業者を探す →
          </Link>
        </div>
      ) : (
        <>
          {inProgress.length > 0 && (
            <>
              <h3 className="font-serif text-deep-green text-lg">受講中の講座</h3>
              <div className="space-y-4">
                {inProgress.map((p) => (
                  <div key={p.id} className="bg-white rounded-2xl overflow-hidden border border-stone-100 flex flex-col md:flex-row">
                    <div className="w-full md:w-44 h-28 md:h-auto relative flex-shrink-0">
                      <img src={getThumbnail(p)} alt={p.product_title} className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 bg-deep-green/20" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 flex items-center justify-center bg-white/90 rounded-full">
                          <i className="ri-play-fill text-deep-green text-xl" />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 p-5">
                      <p className="text-xs text-charcoal/40 font-sans mb-1">{p.category ?? ""} · {p.provider_name}</p>
                      <p className="font-serif text-charcoal text-base mb-3">{p.product_title}</p>
                      <div className="mb-1 flex items-center justify-between text-xs text-charcoal/50 font-sans">
                        <span>進捗 {p.progress ?? 0}%</span>
                        <span>{p.watched_minutes ?? 0}分 / {p.total_minutes ?? 0}分</span>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-teal to-deep-green rounded-full transition-all duration-700"
                          style={{ width: `${p.progress ?? 0}%` }}
                        />
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Link
                          to={`/courses/${p.product_id}`}
                          className="text-xs bg-deep-green text-white px-4 py-2 rounded-full font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap"
                        >
                          続きを見る →
                        </Link>
                        <span className="text-xs text-charcoal/40 font-sans self-center">
                          購入日：{new Date(p.purchased_at).toLocaleDateString("ja-JP")}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {completed.length > 0 && (
            <>
              <h3 className="font-serif text-deep-green text-lg mt-6">完了した講座・教材</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {completed.map((p) => (
                  <div key={p.id} className="bg-white rounded-2xl p-4 border border-stone-100 flex gap-4 items-center">
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 relative">
                      <img src={getThumbnail(p)} alt={p.product_title} className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 bg-deep-green/30 flex items-center justify-center">
                        <i className="ri-checkbox-circle-fill text-white text-xl" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-charcoal/40 font-sans">{p.provider_name}</p>
                      <p className="font-sans text-sm text-charcoal truncate">{p.product_title}</p>
                      <p className="text-xs text-teal font-sans mt-1">
                        {p.product_type === "video" ? `全${p.total_minutes}分` : "教材キット"} · 完了
                      </p>
                    </div>
                    <Link
                      to={`/${p.product_type === "video" ? "courses" : "materials"}/${p.product_id}`}
                      className="text-xs text-teal font-sans whitespace-nowrap cursor-pointer hover:text-deep-green"
                    >
                      再確認 →
                    </Link>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
